import { ExportProgressWrapper } from '@/components/commonComponents/style';
import { useRowSelectionTable } from '@/hooks';
import { BoldTag } from '@/pages/authenPages/styles';
import { useSendSampleKitsForAnalysis } from '@/queries';
import { AnalysisSampleKitEntity } from '@/types';
import { generateOptions } from '@/utils';
import {
  CustomButton,
  CustomCheckbox,
  CustomCheckboxGroup,
} from '@amili-v3/amili-v3-design';
import { Divider, Progress } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SampleKitsCheckboxModal } from '../../styled';

export const streamAnalysisSampleKitsEventName =
  'streamProcessingQuantityAnalysisSampleKits';

type SendForAnalysisSampleKitsModalProps = {
  isOpen: boolean;
  toggleModal: () => void;
  rowSelectionValues: ReturnType<typeof useRowSelectionTable>;
  streamAnalysisSampleKits?: AnalysisSampleKitEntity[];
  loading?: boolean;
  isSendAllSampleKits?: boolean;
};
const SendForAnalysisSampleKitsModal: React.FC<
  SendForAnalysisSampleKitsModalProps
> = props => {
  const {
    isOpen,
    toggleModal,
    rowSelectionValues,
    loading = false,
    streamAnalysisSampleKits = [],
    isSendAllSampleKits = false,
  } = props;
  const { t } = useTranslation();
  const {
    checkedListArray,
    records: sampleKitsRecords,
    setCheckedListOfAllPage,
    setSelectAllRow,
    setAllPageRecords,
  } = rowSelectionValues;
  const { displayId: analysisBatchId = '' } = useParams();

  const [percentProgress, setPercentProgress] = useState(0);
  const [selectedSampleKits, setSelectedSampleKits] = useState<
    AnalysisSampleKitEntity[]
  >([]);
  const [checkedKitSerials, setCheckedKitSerials] = useState<string[]>([]); //lower case format

  // Handle quantity of analysis sample kits signal
  useEffect(() => {
    const handler = (event: Event) => {
      const e = event as CustomEvent;
      setPercentProgress(e.detail?.percentProgress ?? 0);
    };
    window.addEventListener(streamAnalysisSampleKitsEventName, handler);

    return () =>
      window.removeEventListener(streamAnalysisSampleKitsEventName, handler);
  }, []);

  useEffect(() => {
    if (selectedSampleKits.length || !checkedListArray.length || !isOpen)
      return;

    const sampleKitsBySelected: AnalysisSampleKitEntity[] = isSendAllSampleKits
      ? streamAnalysisSampleKits
      : sampleKitsRecords;
    const sampleKitsWithSelectedIds = sampleKitsBySelected.reduce(
      (acc, sampleKit) => {
        if (checkedListArray.includes(sampleKit.id)) {
          acc.push(sampleKit);
        }
        return acc;
      },
      [] as AnalysisSampleKitEntity[],
    );

    setSelectedSampleKits(sampleKitsWithSelectedIds);
  }, [
    checkedListArray,
    isOpen,
    isSendAllSampleKits,
    sampleKitsRecords,
    selectedSampleKits.length,
    streamAnalysisSampleKits,
  ]);

  const sampleKitsOptions = generateOptions(
    selectedSampleKits.map(({ kit }) => kit?.serial?.toUpperCase() ?? ''),
  );
  const checkAllKitSerials =
    sampleKitsOptions.length === checkedKitSerials.length;
  const indeterminate =
    checkedKitSerials.length > 0 &&
    checkedKitSerials.length < sampleKitsOptions.length;

  const handleCloseModal = () => {
    setSelectedSampleKits([]);
    setCheckedKitSerials([]);
    setPercentProgress(0);
    toggleModal();
  };

  const { mutate: sendForAnalysisMutate, isPending: isSendingSampleKits } =
    useSendSampleKitsForAnalysis({
      onSuccess: () => {
        handleCloseModal();
        setCheckedListOfAllPage({});
        setSelectAllRow(false);
        setAllPageRecords([]);
      },
    });

  const handleChangeCheckAll = (e: CheckboxChangeEvent) => {
    setCheckedKitSerials(
      e.target.checked
        ? sampleKitsOptions.map(({ value }) => value.toLowerCase())
        : [],
    );
  };

  const handleSendForAnalysis = () => {
    sendForAnalysisMutate({
      analysisBatchId,
      kitIds: checkedKitSerials,
    });
  };

  return (
    <SampleKitsCheckboxModal
      destroyOnClose
      title={'Select Analysis Sample Kits'}
      open={isOpen}
      onCancel={handleCloseModal}
      bodyStyle={{ overflowY: 'auto', maxHeight: 'min(600px, 50vh)' }}
      footer={
        <CustomButton
          loading={loading || isSendingSampleKits}
          onClick={handleSendForAnalysis}
          size="large"
          width={'100%'}
          disabled={!checkedKitSerials.length}
        >
          {t('common.confirm')}
        </CustomButton>
      }
      centered
      width={450}
    >
      {loading && isSendAllSampleKits ? (
        <ExportProgressWrapper>
          <Progress percent={percentProgress} />
        </ExportProgressWrapper>
      ) : (
        <div>
          <CustomCheckbox
            indeterminate={indeterminate}
            onChange={handleChangeCheckAll}
            checked={checkAllKitSerials}
          >
            <BoldTag>All Kit Serials</BoldTag>
          </CustomCheckbox>
          <Divider style={{ margin: '10px 0px' }} />
          <CustomCheckboxGroup
            rootClassName="sample-kits-checkbox-group"
            options={sampleKitsOptions} //upper case format
            value={checkedKitSerials.map(serial => serial.toUpperCase())}
            onChange={checkedValues => {
              setCheckedKitSerials(
                checkedValues.map(serial => (serial as string).toLowerCase()),
              );
            }}
          />
        </div>
      )}
    </SampleKitsCheckboxModal>
  );
};

export default React.memo(SendForAnalysisSampleKitsModal);
